import React, { useEffect, useMemo, useState } from "react";
import "./styles.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Spin,
  Table,
  Tag,
  Empty,
  notification,
  Form,
  Input,
  Button,
} from "antd";
import { FaRegUser, FaArrowLeft } from "react-icons/fa";
import { MdHistory, MdDownload, MdSubscriptions } from "react-icons/md";
import { VscKey } from "react-icons/vsc";
import moment from "moment";

import Header from "../../component/header";
import Footer from "../../component/footer/footer";
import RenewalModal from "../../components/RenewalModal";
import { API } from "../../Config/API";
import { RootState } from "../../store";
import { changePassword, logout, updateProfile } from "../../store/authSlice";

type SettingsTab =
  | "profile"
  | "change-password"
  | "current-plan"
  | "history";

interface SubscriptionItem {
  id: number;
  status: string;
  subscription_start_date?: string | null;
  subscription_end_date?: string | null;
  payment_date?: string | null;
  amount?: number | string | null;
  payment_method?: string | null;
  payment_mode?: string | null;
  receipt_number?: string | null;
  createdAt?: string;
  subscription_plan?: {
    id?: number;
    plan_name?: string;
    type?: string;
    price?: number;
    duration_type?: string;
    duration_value?: number;
    region?: string;
  } | null;
}

const formatDate = (d?: string | null) =>
  d ? moment(d).format("DD MMM YYYY") : "-";

const isActive = (s: SubscriptionItem) => {
  if (s.status !== "active") return false;
  if (!s.subscription_end_date) return true;
  return new Date(s.subscription_end_date).getTime() >= Date.now();
};

const statusColor = (s: SubscriptionItem) => {
  if (isActive(s)) return "green";
  if (s.status === "cancelled") return "red";
  if (s.status === "refunded") return "purple";
  if (s.status === "expired") return "default";
  return "orange";
};

const canDownloadReceipt = (s: SubscriptionItem) =>
  s.status !== "cancelled" && s.status !== "refunded";

function SettingsScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as SettingsTab | null;
  const activeTab: SettingsTab =
    tabParam === "change-password" ||
    tabParam === "current-plan" ||
    tabParam === "history"
      ? tabParam
      : "profile";

  const { token } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState<SubscriptionItem[]>([]);
  const [showRenewal, setShowRenewal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSubs = async () => {
      setLoading(true);
      try {
        const authToken = token || localStorage.getItem("authToken");
        const res = await fetch(
          `${API.BASE_URL}epaper-users/my-subscriptions`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error?.message || "Failed");
        if (!cancelled) setSubs(json?.data || []);
      } catch (e: any) {
        if (!cancelled) {
          notification.error({
            message: "Error",
            description: e?.message || "Failed to load subscriptions",
            placement: "topRight",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSubs();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const activeSub = useMemo(() => {
    const active = subs.find(isActive);
    if (active) return active;
    return subs[0] || null;
  }, [subs]);

  const setTab = (tab: SettingsTab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="Subscription-page">
      <Header onChange={() => {}} />

      <div className="Subscription-back-row">
        <button
          type="button"
          className="Subscription-back-btn"
          onClick={() => navigate("/")}
        >
          <FaArrowLeft size={12} />
          Back to Home
        </button>
      </div>

      <div className="Subscription-layout">
        <aside className="Subscription-sidebar">
          <button
            className={`Subscription-sidebar-item${
              activeTab === "profile" ? " active" : ""
            }`}
            onClick={() => setTab("profile")}
          >
            <FaRegUser size={16} />
            Profile
          </button>
          <button
            className={`Subscription-sidebar-item${
              activeTab === "change-password" ? " active" : ""
            }`}
            onClick={() => setTab("change-password")}
          >
            <VscKey size={16} />
            Change Password
          </button>
          <button
            className={`Subscription-sidebar-item${
              activeTab === "current-plan" ? " active" : ""
            }`}
            onClick={() => setTab("current-plan")}
          >
            <MdSubscriptions size={18} />
            Current Plan
          </button>
          <button
            className={`Subscription-sidebar-item${
              activeTab === "history" ? " active" : ""
            }`}
            onClick={() => setTab("history")}
          >
            <MdHistory size={18} />
            Subscription History
          </button>
        </aside>

        <main className="Subscription-content">
          {activeTab === "profile" ? (
            <ProfileView />
          ) : activeTab === "change-password" ? (
            <ChangePasswordView />
          ) : loading ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin />
            </div>
          ) : activeTab === "current-plan" ? (
            <DetailsView
              sub={activeSub}
              onRenew={() => setShowRenewal(true)}
            />
          ) : (
            <HistoryView subs={subs} token={token} />
          )}
        </main>
      </div>

      <RenewalModal
        visible={showRenewal}
        onClose={() => setShowRenewal(false)}
      />

      <Footer />
    </div>
  );
}

function DetailsView({
  sub,
  onRenew,
}: {
  sub: SubscriptionItem | null;
  onRenew: () => void;
}) {
  if (!sub) {
    return (
      <>
        <div className="Subscription-content-title">Current Plan</div>
        <div className="Subscription-empty">
          <Empty description="No subscription found" />
          <div style={{ marginTop: 16 }}>
            <Button type="primary" size="large" onClick={onRenew}>
              Subscribe Now
            </Button>
          </div>
        </div>
      </>
    );
  }

  const plan = sub.subscription_plan;
  const active = isActive(sub);
  const daysLeft = sub.subscription_end_date
    ? Math.max(
        0,
        Math.ceil(
          (new Date(sub.subscription_end_date).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : null;

  const isExpired = !active;
  const isExpiringSoon = active && daysLeft !== null && daysLeft <= 3;

  return (
    <>
      <div className="Subscription-content-title">Current Plan</div>
      <div className="Subscription-detail-card">
        <div className="Subscription-detail-row">
          <span className="Subscription-detail-label">Status</span>
          <span className="Subscription-detail-value">
            <Tag color={statusColor(sub)}>
              {active ? "Active" : (sub.status || "-").toUpperCase()}
            </Tag>
          </span>
        </div>
        <div className="Subscription-detail-row">
          <span className="Subscription-detail-label">Plan</span>
          <span className="Subscription-detail-value">
            {plan?.plan_name || "-"}
          </span>
        </div>
        <div className="Subscription-detail-row">
          <span className="Subscription-detail-label">Type</span>
          <span className="Subscription-detail-value">
            {plan?.type || "-"}
          </span>
        </div>
        {plan?.region && (
          <div className="Subscription-detail-row">
            <span className="Subscription-detail-label">Region</span>
            <span className="Subscription-detail-value">{plan.region}</span>
          </div>
        )}
        <div className="Subscription-detail-row">
          <span className="Subscription-detail-label">Start Date</span>
          <span className="Subscription-detail-value">
            {formatDate(sub.subscription_start_date)}
          </span>
        </div>
        <div className="Subscription-detail-row">
          <span className="Subscription-detail-label">End Date</span>
          <span className="Subscription-detail-value">
            {formatDate(sub.subscription_end_date)}
          </span>
        </div>
        {daysLeft !== null && active && (
          <div className="Subscription-detail-row">
            <span className="Subscription-detail-label">Days Remaining</span>
            <span className="Subscription-detail-value">{daysLeft} days</span>
          </div>
        )}
        {sub.amount != null && (
          <div className="Subscription-detail-row">
            <span className="Subscription-detail-label">Amount Paid</span>
            <span className="Subscription-detail-value">{sub.amount}</span>
          </div>
        )}
        {(sub.payment_mode || sub.payment_method) && (
          <div className="Subscription-detail-row">
            <span className="Subscription-detail-label">Payment Method</span>
            <span className="Subscription-detail-value">
              {sub.payment_mode || sub.payment_method}
            </span>
          </div>
        )}
      </div>

      <div
        className={`Subscription-renew-block${
          isExpired ? " expired" : isExpiringSoon ? " expiring" : ""
        }`}
      >
        <div className="Subscription-renew-text">
          <div className="Subscription-renew-title">
            {isExpired
              ? "Your subscription has expired"
              : isExpiringSoon
              ? `Your subscription expires in ${daysLeft} day${
                  daysLeft === 1 ? "" : "s"
                }`
              : "Want a different plan or to extend early?"}
          </div>
          <div className="Subscription-renew-sub">
            {isExpired
              ? "Renew now to regain access to all editions."
              : isExpiringSoon
              ? "Renew now so your access continues without interruption."
              : "Browse available plans to renew or upgrade your subscription."}
          </div>
        </div>
        <Button
          type="primary"
          size="large"
          danger={isExpired}
          onClick={onRenew}
        >
          {isExpired || isExpiringSoon ? "Renew Now" : "Renew or Upgrade"}
        </Button>
      </div>
    </>
  );
}

function HistoryView({
  subs,
  token,
}: {
  subs: SubscriptionItem[];
  token: string | null;
}) {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const handleDownload = async (sub: SubscriptionItem) => {
    setDownloadingId(sub.id);
    try {
      const authToken = token || localStorage.getItem("authToken");
      const res = await fetch(
        `${API.BASE_URL}epaper-users/my-subscriptions/${sub.id}/receipt`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!res.ok) {
        let msg = "Failed to download receipt";
        try {
          const j = await res.json();
          msg = j?.error?.message || msg;
        } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeNo = (sub.receipt_number || String(sub.id)).replace(/\//g, "-");
      a.download = `${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      notification.error({
        message: "Error",
        description: e?.message || "Failed to download receipt",
        placement: "topRight",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      title: "Sl No",
      key: "sl_no",
      width: 70,
      render: (_: any, __: SubscriptionItem, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Plan",
      key: "plan",
      render: (_: any, row: SubscriptionItem) =>
        row.subscription_plan?.plan_name || "-",
    },
    {
      title: "Start",
      dataIndex: "subscription_start_date",
      key: "start",
      render: (d: string) => formatDate(d),
    },
    {
      title: "End",
      dataIndex: "subscription_end_date",
      key: "end",
      render: (d: string) => formatDate(d),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (d: string) => formatDate(d),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v: any) => (v != null ? v : "-"),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, row: SubscriptionItem) => (
        <Tag color={statusColor(row)}>
          {isActive(row) ? "Active" : (row.status || "-").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Receipt",
      key: "receipt",
      render: (_: any, row: SubscriptionItem) =>
        canDownloadReceipt(row) ? (
          <Button
            size="small"
            icon={<MdDownload />}
            loading={downloadingId === row.id}
            onClick={() => handleDownload(row)}
          >
            Download
          </Button>
        ) : (
          <span style={{ color: "#aaa", fontSize: 12 }}>—</span>
        ),
    },
  ];

  return (
    <>
      <div className="Subscription-content-title">Subscription History</div>
      {subs.length === 0 ? (
        <div className="Subscription-empty">
          <Empty description="No subscription history" />
        </div>
      ) : (
        <Table
          columns={columns as any}
          dataSource={subs}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: (current, pageSize) =>
              setPagination({ current, pageSize }),
          }}
          scroll={{ x: 800 }}
        />
      )}
    </>
  );
}

function ProfileView() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      full_name: user?.full_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
    });
  }, [user, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const payload: any = {};
      if ((values.full_name || "").trim() !== (user?.full_name || "")) {
        payload.full_name = values.full_name.trim();
      }
      if ((values.email || "").trim().toLowerCase() !== (user?.email || "")) {
        payload.email = values.email.trim().toLowerCase();
      }
      if ((values.phone || "").trim() !== (user?.phone || "")) {
        payload.phone = values.phone.trim();
      }

      if (Object.keys(payload).length === 0) {
        notification.info({
          message: "No changes",
          description: "There are no changes to save.",
          placement: "topRight",
        });
        setLoading(false);
        return;
      }

      const result = await (dispatch as any)(updateProfile(payload)).unwrap();

      notification.success({
        message: "Success",
        description: result.message || "Profile updated successfully",
        placement: "topRight",
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error || "Failed to update profile",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="Subscription-content-title">Profile</div>
      <div style={{ maxWidth: 460 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Full Name"
            name="full_name"
            rules={[
              { required: true, message: "Please enter your name" },
              {
                validator: (_, value) =>
                  value && value.trim().length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Name cannot be empty")),
              },
            ]}
          >
            <Input placeholder="Enter your name" size="large" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter your email" size="large" />
          </Form.Item>

          <Form.Item
            label="Mobile Number"
            name="phone"
            rules={[
              { required: true, message: "Please enter your mobile number" },
              {
                pattern: /^[+0-9\s-]{7,20}$/,
                message: "Please enter a valid mobile number",
              },
            ]}
          >
            <Input placeholder="Enter your mobile number" size="large" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}

function ChangePasswordView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const result = await (dispatch as any)(
        changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        })
      ).unwrap();

      notification.success({
        message: "Success",
        description: result.message || "Password changed successfully",
        placement: "topRight",
      });

      form.resetFields();
      dispatch(logout());
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      navigate("/login");
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error || "Failed to change password",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="Subscription-content-title">Change Password</div>
      <div style={{ maxWidth: 460 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[
              {
                required: true,
                message: "Please enter your current password",
              },
            ]}
          >
            <Input.Password placeholder="Enter current password" size="large" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter a new password" },
              { min: 8, message: "Password must be at least 8 characters" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const currentPassword = getFieldValue("currentPassword");
                  if (!value || currentPassword !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      "New password must be different from current password"
                    )
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Enter new password" size="large" />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match")
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" size="large" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}

export default SettingsScreen;
